import { useEffect, useState } from "react";
import { Link2, AlertTriangle, CircleCheck, RefreshCw } from "lucide-react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * LabFloStatusBanner — sits above the Order Queue and tells the prescriber
 * (in plain English) whether ERX submissions will actually transmit to
 * Polomar via LabFlo right now.
 *
 * Reads `GET /api/pharmacy-tenants/status` (admin/prescriber-only) which
 * returns `{ source, age_seconds, count }`. Source values:
 *   • "lifefile"      → LifeFile Polomar partner is active + configured (GREEN)
 *   • "labflo"        → live API hit just succeeded     (GREEN)
 *   • "cache"         → in-process cache, recent hit    (GREEN)
 *   • "stale_cache"   → LabFlo failed but we have data  (YELLOW)
 *   • "db_fallback"   → reading from local Mongo only   (RED)
 *
 * Hidden entirely when source is healthy + count > 0 to keep the dashboard
 * clean. Only renders when there's a problem worth surfacing OR when an
 * admin clicks the "Recheck" button.
 */
export default function LabFloStatusBanner() {
  const [status, setStatus] = useState(null); // { source, count, age_seconds } | null
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [show, setShow] = useState(false); // admin toggle to force-show even when healthy

  const load = async () => {
    try {
      const { data } = await API.get("/pharmacy-tenants/status");
      setStatus(data);
    } catch (_e) {
      setStatus({ source: "unreachable", count: 0 });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // Re-check every 5 min so a long-lived dashboard surfaces drift.
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  if (loading || !status) return null;

  const src = status.source || "unreachable";
  const isHealthy = (src === "lifefile" || src === "labflo" || src === "cache") && (status.count || 0) > 0;

  // Healthy + we're not forcing visibility → render compact green pill only,
  // so the prescriber has a quiet "yes it's working" confirmation without
  // a full banner taking up dashboard space.
  if (isHealthy && !show) {
    const pillLabel = src === "lifefile" ? "LifeFile connected" : "Polomar connected";
    return (
      <div className="flex items-center justify-end mb-3" data-testid="labflo-status-pill">
        <button
          onClick={() => setShow(true)}
          className="inline-flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 dark:text-emerald-300 dark:bg-emerald-900/30 dark:border-emerald-800 px-2.5 py-1 rounded-full hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition"
          title={`Source: ${src} · ${status.count} pharmacies${status.age_seconds != null ? ` · ${status.age_seconds}s old` : ""}`}
        >
          <CircleCheck className="w-3 h-3" /> {pillLabel}
        </button>
      </div>
    );
  }

  // Yellow / red banner state
  const isStale = src === "stale_cache";
  const isDown = src === "db_fallback" || src === "unreachable" || (status.count || 0) === 0;

  const palette = isDown
    ? { border: "border-red-300 dark:border-red-800", bg: "bg-red-50 dark:bg-red-900/20", icon: "text-red-600 dark:text-red-400", title: "text-red-900 dark:text-red-200", body: "text-red-800 dark:text-red-300" }
    : isStale
      ? { border: "border-amber-300 dark:border-amber-800", bg: "bg-amber-50 dark:bg-amber-900/20", icon: "text-amber-600 dark:text-amber-400", title: "text-amber-900 dark:text-amber-200", body: "text-amber-800 dark:text-amber-300" }
      : { border: "border-slate-300 dark:border-slate-700", bg: "bg-slate-50 dark:bg-slate-800/50", icon: "text-slate-500", title: "text-slate-700 dark:text-slate-200", body: "text-slate-600 dark:text-slate-400" };

  const headline = isDown
    ? "Pharmacy connection is unreachable — eRx transmissions will NOT reach Polomar."
    : isStale
      ? "Pharmacy service is temporarily unreachable — showing last cached pharmacy list."
      : "Polomar / LifeFile connection state";

  const detail = isDown
    ? `Source: ${src} · ${status.count} pharmacies cached locally. Approving an eRx will save it in our database but Polomar will NOT receive it until the connection is back online. Check the LifeFile partner config (Admin → Pharmacy) and its Active flag.`
    : isStale
      ? `Cached ${status.age_seconds != null ? `${Math.round(status.age_seconds / 60)} min` : "—"} ago. Existing data is being served; new pharmacies/changes won't appear until the service recovers.`
      : `Source: ${src} · ${status.count} pharmacies.`;

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await API.get("/pharmacy-tenants?refresh=true");
    } catch (_e) { /* ignore */ }
    await load();
  };

  return (
    <div
      className={`mb-4 rounded-xl border ${palette.border} ${palette.bg} px-4 py-3 flex items-start gap-3`}
      data-testid="labflo-status-banner"
    >
      {isDown ? (
        <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${palette.icon}`} />
      ) : (
        <Link2 className={`w-5 h-5 mt-0.5 shrink-0 ${palette.icon}`} />
      )}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${palette.title}`} data-testid="labflo-status-headline">
          {headline}
        </p>
        <p className={`text-xs mt-0.5 ${palette.body}`} data-testid="labflo-status-detail">
          {detail}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          size="sm"
          variant="outline"
          onClick={handleRefresh}
          disabled={refreshing}
          className="rounded-lg text-xs h-7"
          data-testid="labflo-recheck-btn"
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? "animate-spin" : ""}`} />
          Recheck
        </Button>
        {isHealthy && (
          <button
            onClick={() => setShow(false)}
            className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            data-testid="labflo-status-hide-btn"
          >
            Hide
          </button>
        )}
      </div>
    </div>
  );
}
